import * as Sentry from "@sentry/react";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { APP_VERSION, APP_ENVIRONMENT } from './config/env';

// Only initialize Sentry in non-development environments
if (import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // VITE_SENTRY_ENVIRONMENT is set per deploy target in CI (development,
    // demo, production). MODE is always "production" for built artifacts, so
    // it can't distinguish environments on its own.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    // Tie every event to the build it came from (see Settings footer).
    release: APP_VERSION,

    // Sentry operates under a BAA — PII collection is authorized
    sendDefaultPii: true,

    ignoreErrors: [
      // Microsoft's link scanners (Outlook SafeLinks / Defender) load the
      // page headlessly with an injected script that rejects with this; the
      // events have no frames, no interaction and a Microsoft IP.
      /Object Not Found Matching Id:\d+, MethodName:/,
    ],

    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Session Replay records every page. Its MutationObserver serializes
      // every DOM change, and SuperForm documents render 1000+ nodes per
      // radio/select interaction (measured on production: 618 ms per radio
      // click with Replay on vs 93 ms off), so a build can opt the document
      // editor out with VITE_DISABLE_REPLAY_IN_DOCUMENT_EDITOR=true — see
      // src/lib/sentry-replay.ts.
      // The mutation caps drop a runaway replay instead of stalling the UI.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        mutationLimit: 1500,
        mutationBreadcrumbLimit: 500,
      }),
      // Requires Document-Policy: js-profiling response header (configured in CloudFront).
      // Silently no-ops in local dev and non-Chromium browsers.
      Sentry.browserProfilingIntegration(),
    ],

    // Tracing
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.monami\.(io|test)/,
    ],

    // Profiling
    profilesSampleRate: import.meta.env.MODE === "production" ? 0.2 : 1.0,

    // Session Replay. Every session is recorded outside production; in
    // production a sample is recorded continuously and the rest buffer the
    // last ~60s so an error always arrives with a replay attached. Keyed off
    // APP_ENVIRONMENT rather than MODE because MODE is "production" for every
    // built artifact, demo included.
    replaysSessionSampleRate: APP_ENVIRONMENT === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Logging
    enableLogs: true,
  });
}
